import styled from 'styled-components';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { TooltipStyle } from './tooltip-style';
import { FormattedMoney } from '../atomic-components';
import { BarData } from './use-bank-history-selection';
import { addAlpha } from '@algoan/deep-dive/theme';
import { useElementWidth } from '@algoan/dashboard/utils';

const tooltipWidth = 248;
const minTableRowWidth = 114;
const graphPadding = 38;

interface BalanceRangeTooltipProps {
  endData: BarData;
  originData: BarData;
  totalTransactions: number;
  width: number;
  x: number;
  graphWidth: number;
}

export const BalanceRangeTooltip: React.FC<BalanceRangeTooltipProps> = ({
  endData,
  originData,
  totalTransactions,
  x,
  width,
  graphWidth,
}) => {
  const { elementRef, elementWidth } = useElementWidth<HTMLTableDataCellElement>();

  const firstDate = originData.date < endData.date ? originData : endData;
  const lastDate = originData.date < endData.date ? endData : originData;
  const extraWidth = elementWidth > minTableRowWidth ? elementWidth - minTableRowWidth : 0;

  const getXPosition = (): number => {
    const startPosition = x + (width - tooltipWidth - extraWidth) / 2;

    if (startPosition < graphPadding) return graphPadding;
    if (startPosition > graphWidth - tooltipWidth - graphPadding) return graphWidth - tooltipWidth - graphPadding;
    return startPosition;
  };

  return (
    <foreignObject x={getXPosition()} y={-56} width={tooltipWidth} height="30" overflow="visible">
      <S.Tooltip width={tooltipWidth + extraWidth}>
        <S.Date>
          <FormattedDate value={firstDate.date} day="numeric" month="numeric" />
          <S.DateSeparator />
          <FormattedDate value={lastDate.date} day="numeric" month="numeric" />
        </S.Date>
        <S.Divider />
        <S.BalanceAndDifference>
          <tbody>
            <tr>
              <S.Text>
                <FormattedMessage
                  id="bank_history_chart.tooltip.balance_with_date"
                  defaultMessage="Balance at"
                  description="Label of the balance at a specific date in the BankHistoryChart range tooltip"
                />{' '}
                <FormattedDate value={firstDate.date} day="numeric" month="numeric" />
              </S.Text>
              <S.Value isPositive={firstDate.balance > 0}>
                <FormattedMoney number={firstDate.balance} />
              </S.Value>
            </tr>
            <tr>
              <S.Text>
                <FormattedMessage
                  id="bank_history_chart.tooltip.balance_with_date"
                  defaultMessage="Balance at"
                  description="Label of the balance at a specific date in the BankHistoryChart range tooltip"
                />{' '}
                <FormattedDate value={lastDate.date} day="numeric" month="numeric" />
              </S.Text>
              <S.Value isPositive={lastDate.balance > 0}>
                <FormattedMoney number={lastDate.balance} />
              </S.Value>
            </tr>
            <tr>
              <S.Text>
                <FormattedMessage
                  id="bank_history_chart.tooltip.evolution"
                  defaultMessage="Evolution"
                  description="Label of the evolution row in the BankHistoryChart range tooltip"
                />
              </S.Text>
              <S.Value ref={elementRef} isPositive={totalTransactions > 0}>
                <FormattedMoney number={totalTransactions} />
              </S.Value>
            </tr>
          </tbody>
        </S.BalanceAndDifference>
      </S.Tooltip>
    </foreignObject>
  );
};

export const S = {
  Tooltip: styled(TooltipStyle.Tooltip)<{ width: number }>`
    align-items: center;
    height: 68px;
    position: absolute;
    width: ${({ width }) => width}px;
    border-radius: 5px;
    box-shadow: 0px 1px 15px 0px #00466b26;
  `,
  Date: styled(TooltipStyle.Date)`
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.algoanBlue};
    font-weight: 600;
    line-height: 15px;
    margin: 9px 8px;
  `,
  DateSeparator: styled.div`
    margin: 5px auto;
    height: 10px;
    width: 1px;
    background-color: ${({ theme }) => theme.secondary.softBlue1};
  `,
  Divider: styled(TooltipStyle.Divider)`
    height: 68px;
    background-color: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
  `,
  BalanceAndDifference: styled.table`
    border-collapse: collapse;
    font-size: 9px;
    line-height: 11px;
    margin: 7px 5px;
    min-width: 189px;
  `,
  Text: styled.td`
    color: ${({ theme }) => theme.algoanBlue};
    text-align: left;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
  `,
  Value: styled.td<{ isPositive: boolean }>`
    color: ${({ isPositive, theme }) =>
      isPositive ? theme.scoreIndicatorColors.positiveValue : theme.scoreIndicatorColors.negativeValue};
    font-size: 13px;
    font-weight: 800;
    padding: 1px 0;
    text-align: right;
    white-space: nowrap;
  `,
};
